import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import AppModalCard from "../../../Components/shared/AppModalCard.js"
import AppModalHeader from "../../../Components/shared/AppModalHeader.js"
import AppModalShell from "../../../Components/shared/AppModalShell.js"
import Button from "../../../Components/Buttons/Button.js"
import { CatIconOutlined, DogIconOutlined } from "../../../shared/Icons/CustomIcons.js"
import { useCoursesApi } from "../useCoursesApi.js"
import { buildCourseUrl } from "../returnPath.js"
import { S } from "../strings.js"
import type { CourseKey } from "../types.js"

/** Dismissible, never-blocking prompt offering the Adopter Course. Dismissal is stored server-side. */
export default function AdopterCoursePrompt({ onDone }: { onDone: () => void }) {
  useTranslation("courses")
  const api = useCoursesApi()
  const navigate = useNavigate()
  const [closing, setClosing] = useState(false)

  function finish(then?: () => void) {
    if (closing) return
    setClosing(true)
    // Fire-and-forget: a failed dismiss must never trap the user in the prompt.
    void api.dismissAdopterPrompt().catch(() => undefined)
    onDone()
    then?.()
  }

  function open(courseKey: CourseKey) {
    finish(() => navigate(buildCourseUrl({ courseKey })))
  }

  return (
    <AppModalShell onBackdropClick={() => finish()} overlayClassName="bg-black/40" mobileSheet zIndexClassName="z-[9000]">
      <AppModalCard title={S.prompt.title} size="md" mobileSheet panelClassName="p-0">
        <AppModalHeader title={S.prompt.title} onClose={() => finish()} titleClassName="text-base font-semibold text-my-black" />
        <div className="space-y-4 p-5" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 1.25rem)" }}>
          <p className="text-sm leading-relaxed text-gray-600">{S.prompt.body}</p>
          <div className="grid grid-cols-2 gap-3">
            <Button onClick={() => open("adopter-dog")} size="lg" className="min-h-[52px]">
              <DogIconOutlined size={20} fill="currentColor" />
              {S.prompt.dog}
            </Button>
            <Button onClick={() => open("adopter-cat")} size="lg" className="min-h-[52px]">
              <CatIconOutlined size={20} fill="currentColor" />
              {S.prompt.cat}
            </Button>
          </div>
          <Button onClick={() => finish()} variant="ghost" size="lg" fullWidth className="min-h-[44px]">
            {S.prompt.dismiss}
          </Button>
        </div>
      </AppModalCard>
    </AppModalShell>
  )
}
